/**
 * @file The one read-side `layout` switch: turns one attachment version into
 * either a URL (the object lane) or an already-assembled, already-verified
 * `Blob` (the chunked lane). Consumers: `useAttachmentDownload`, which fetches
 * the URL, and `AttachmentPreviewOverlay`, which hands the URL to an `<iframe>`
 * or the Office viewer and the blob to an object URL.
 *
 * Invariants: the switch precedes every storage-path read, because the
 * attachment's top-level path mirrors the winning version and for a chunked
 * winner names either nothing or another version's bytes; a chunked version
 * never falls through to the object lane's fallbacks; the object lane's order is
 * the version's path, the attachment's path, the version's stored URL, the
 * attachment's stored URL. Both funnels share this file so the switch cannot
 * drift between a download and a preview of the same version.
 *
 * `signal` and `onProgress` are the chunked lane's alone — the object lane ends
 * at `getDownloadURL`, which transfers no bytes and cannot be stopped — so
 * `onProgress` never fires for an object version rather than firing a
 * manufactured figure. An object version's real progress belongs to whoever
 * fetches the URL: `useAttachmentDownload` streams the response and counts it
 * there.
 */
import { ref, getDownloadURL } from 'firebase/storage';
import type { Attachment, AttachmentVersion } from '#types/System.types';
import { storage } from '../services/startStorage';
import {
  readChunkedAttachmentVersion,
  type ChunkedAttachmentReadProgress,
} from './chunkedAttachment';

/**
 * Where a version's bytes come from. A chunked version has no URL to hand an
 * `<iframe>` or a `fetch`, so it arrives as bytes; an object version arrives as
 * a URL, which the caller may use directly or fetch.
 */
export type AttachmentVersionSource =
  | { kind: 'url'; url: string }
  | { kind: 'blob'; blob: Blob; contentType: string };

/** What resolving one version's bytes needs. */
export interface AttachmentVersionSourceRequest {
  /** The attachment document id; chunk and manifest paths hang off it. */
  fileUid: string;
  /** The version being read. Pins the manifest to its own version. */
  versionId: string;
  /**
   * The version record, or `undefined` when the document's `versions` map no
   * longer names `versionId` — in which case only the attachment-level
   * fallbacks remain.
   */
  version: AttachmentVersion | undefined;
  /** The denormalized top level of `attachments/{fileUid}`, used as fallback. */
  attachment: Pick<Attachment, 'storagePath' | 'storageUrl'>;
  /** Named in error messages only, so the user sees the file they asked for. */
  fileName: string;
  /** Cancels a chunked read; the object lane has nothing it can stop. */
  signal?: AbortSignal;
  /** Chunked reads only: see `ChunkedAttachmentVersionRequest.onProgress`. */
  onProgress?: (progress: ChunkedAttachmentReadProgress) => void;
}

export const resolveAttachmentVersionSource = async ({
  fileUid,
  versionId,
  version,
  attachment,
  fileName,
  signal,
  onProgress,
}: AttachmentVersionSourceRequest): Promise<AttachmentVersionSource> => {
  if (version?.layout === 'chunked') {
    if (!version.manifestPath) {
      throw new Error('This version is stored in chunks but names no manifest.');
    }
    const contentType = version.type || 'application/octet-stream';
    return {
      kind: 'blob',
      contentType,
      blob: await readChunkedAttachmentVersion({
        fileUid,
        versionId,
        manifestPath: version.manifestPath,
        fileName,
        contentType,
        signal,
        onProgress,
      }),
    };
  }

  const storagePath = version?.storagePath || attachment.storagePath;
  if (storagePath) {
    return { kind: 'url', url: await getDownloadURL(ref(storage, storagePath)) };
  }

  const storedUrl = version?.storageUrl || attachment.storageUrl;
  if (storedUrl) {
    return { kind: 'url', url: storedUrl };
  }

  throw new Error('Storage path not found for this version.');
};
